/**
 * Tenant DB migration page — Iter 325 (2026-02-20).
 *
 * The user does NOT have shell access. This page is the operator's
 * "one-click" entry to safely copy every tenant's data into its own
 * `tenant_<id>` MongoDB database, ahead of flipping the
 * `TENANT_DB_PER_TENANT` flag on production.
 *
 * Two buttons: Dry-Run (safe, reads only) and Apply Migration (copies,
 * NEVER deletes source data). Owner-scoped to the platform tenant
 * ("hidroverse") — the backend endpoint enforces the same guard.
 */
import { useState } from "react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import {
  Database, ShieldCheck, PlayCircle, Loader2, AlertTriangle,
  CheckCircle2, RefreshCw,
} from "lucide-react";

export default function TenantDBMigration() {
  const [busy, setBusy] = useState(null); // "dry" | "apply" | null
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  async function run(mode) {
    setBusy(mode);
    setError("");
    setReport(null);
    try {
      const url = mode === "apply"
        ? "/_geminii/admin/migrate-to-per-tenant-dbs?apply=true"
        : "/_geminii/admin/migrate-to-per-tenant-dbs";
      const resp = await api.post(url, {}, { timeout: 15 * 60 * 1000 }); // 15 min max
      setReport(resp.data);
      toast.success(
        mode === "apply"
          ? "Migration complete. Data has been copied."
          : "Dry-run complete. Review the report below.",
      );
    } catch (e) {
      // [Iter 326] Show the ACTUAL error payload from the server so the
      // operator can diagnose without opening browser DevTools. Falls
      // back to generic message only when nothing else is available.
      const parts = [];
      if (e?.response?.status) parts.push(`HTTP ${e.response.status}`);
      if (e?.response?.data?.detail) parts.push(String(e.response.data.detail));
      else if (typeof e?.response?.data === "string") parts.push(e.response.data);
      else if (e?.response?.data) parts.push(JSON.stringify(e.response.data));
      if (parts.length === 0 && e?.message) parts.push(e.message);
      setError(parts.join(" · ") || "Unknown error");
      toast.error(`Migration ${mode === "apply" ? "apply" : "dry-run"} failed`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="min-h-screen bg-[#0d1220] text-white p-6" data-testid="tenant-migration-page">
      <div className="max-w-4xl mx-auto space-y-6">
        <header className="flex items-center gap-3 border-b border-white/10 pb-4">
          <Database className="w-6 h-6 text-[#1FB6C1]" />
          <div>
            <h1 className="text-2xl font-semibold">Per-Tenant Database Migration</h1>
            <p className="text-sm text-[#a0aabf]">
              One-time copy of every tenant&apos;s data into its own isolated MongoDB
              database. Safe. Reversible. Zero downtime.
            </p>
          </div>
        </header>

        {/* Step 1 — Dry run */}
        <section className="rounded-lg border border-white/10 bg-white/[0.03] p-5 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-[#1FB6C1]/20 text-[#1FB6C1] flex items-center justify-center font-semibold text-sm">1</div>
            <h2 className="text-lg font-semibold">Dry-run (safe — reads only)</h2>
          </div>
          <p className="text-sm text-[#a0aabf]">
            Click the button below. This will scan every tenant and report how
            many rows WOULD be copied. <strong>Nothing is written.</strong>
          </p>
          <button
            type="button"
            onClick={() => run("dry")}
            disabled={busy !== null}
            className="hv-btn hv-btn-primary text-sm inline-flex items-center gap-2"
            data-testid="tenant-migration-dry-run-btn"
          >
            {busy === "dry"
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Running dry-run…</>
              : <><PlayCircle className="w-4 h-4" /> Start dry-run</>}
          </button>
        </section>

        {/* Step 2 — Apply */}
        <section className="rounded-lg border border-white/10 bg-white/[0.03] p-5 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-300 flex items-center justify-center font-semibold text-sm">2</div>
            <h2 className="text-lg font-semibold">Apply the migration</h2>
          </div>
          <p className="text-sm text-[#a0aabf]">
            After you&apos;re happy with the dry-run report, click below to
            actually copy the rows. This copies — <strong>the original data is
            never deleted.</strong> May take 1–5 minutes.
          </p>
          <button
            type="button"
            onClick={() => {
              if (!window.confirm(
                "This will COPY every tenant's rows into their own database.\n\n"
                + "• Original data stays untouched (copy, not move).\n"
                + "• Runs 1–5 minutes.\n"
                + "• Zero downtime; site stays online.\n\n"
                + "Proceed?"
              )) return;
              run("apply");
            }}
            disabled={busy !== null}
            className="hv-btn text-sm inline-flex items-center gap-2 bg-amber-500/20 border-amber-400/40 text-amber-100 hover:bg-amber-500/30"
            data-testid="tenant-migration-apply-btn"
          >
            {busy === "apply"
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Copying rows… stay on this page</>
              : <><ShieldCheck className="w-4 h-4" /> Apply migration</>}
          </button>
        </section>

        {/* Step 3 — Flip flag */}
        <section className="rounded-lg border border-white/10 bg-white/[0.03] p-5 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center font-semibold text-sm">3</div>
            <h2 className="text-lg font-semibold">Flip the flag in Emergent Secrets</h2>
          </div>
          <div className="text-sm text-[#cbd2df] space-y-2">
            <p>Once step 2 finishes successfully:</p>
            <ol className="list-decimal ml-5 space-y-1 text-[#a0aabf]">
              <li>Open your <strong>Emergent dashboard</strong> in a new tab.</li>
              <li>Go to <strong>Manage Deployment → Custom Keys</strong> (Secrets UI).</li>
              <li>Add or edit the key:
                <div className="my-2 bg-black/40 rounded px-3 py-2 font-mono text-xs">
                  <div>Key:&nbsp;&nbsp; <span className="text-[#1FB6C1]">TENANT_DB_PER_TENANT</span></div>
                  <div>Value: <span className="text-emerald-300">true</span></div>
                </div>
              </li>
              <li>Save. Backend auto-restarts (~30 sec).</li>
              <li>Log in as owner + as a technician to verify all data + correct branding.</li>
            </ol>
          </div>
        </section>

        {/* Rollback */}
        <section className="rounded-lg border border-rose-400/30 bg-rose-500/[0.05] p-5 space-y-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-300" />
            <h2 className="text-lg font-semibold text-rose-100">If anything looks wrong — rollback in 30 seconds</h2>
          </div>
          <ol className="list-decimal ml-5 space-y-1 text-sm text-[#cbd2df]">
            <li>Emergent Secrets UI → set <code className="text-rose-200">TENANT_DB_PER_TENANT</code> back to <code className="text-rose-200">false</code>.</li>
            <li>Save. Backend restarts. You&apos;re back on the legacy shared DB. Nothing is lost — the migration only COPIED.</li>
            <li>Message the engineering team with what looked wrong.</li>
          </ol>
        </section>

        {/* Report display */}
        {(report || error) && (
          <section className="rounded-lg border border-white/10 bg-white/[0.03] p-5 space-y-3" data-testid="tenant-migration-report">
            <div className="flex items-center gap-2 justify-between">
              <div className="flex items-center gap-2">
                {error
                  ? <AlertTriangle className="w-5 h-5 text-rose-300" />
                  : <CheckCircle2 className="w-5 h-5 text-emerald-300" />}
                <h2 className="text-lg font-semibold">
                  {error ? "Failed" : `Report — ${report.mode === "apply" ? "APPLY" : "DRY-RUN"}`}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => { setReport(null); setError(""); }}
                className="hv-btn hv-btn-ghost text-xs"
              >
                <RefreshCw className="w-3 h-3" /> Clear
              </button>
            </div>
            {error && (
              <div className="text-sm text-rose-200 bg-rose-500/10 rounded px-3 py-2">
                {error}
              </div>
            )}
            {report && (
              <>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-black/30 rounded p-3">
                    <div className="text-[#a0aabf] text-xs uppercase tracking-wide">Total rows {report.mode === "apply" ? "copied" : "found"}</div>
                    <div className="text-2xl font-semibold text-[#1FB6C1]">{report.total_rows?.toLocaleString?.() ?? report.total_rows}</div>
                  </div>
                  <div className="bg-black/30 rounded p-3">
                    <div className="text-[#a0aabf] text-xs uppercase tracking-wide">Tenants processed</div>
                    <div className="text-2xl font-semibold text-emerald-300">{Object.keys(report.tenants || {}).length}</div>
                  </div>
                </div>
                <details className="text-xs">
                  <summary className="cursor-pointer text-[#1FB6C1] hover:underline py-1">Per-tenant breakdown</summary>
                  <div className="mt-2 space-y-2">
                    {Object.entries(report.tenants || {}).map(([tid, info]) => (
                      <div key={tid} className="border border-white/5 rounded p-3 bg-black/20">
                        <div className="flex justify-between font-mono">
                          <span className="text-[#1FB6C1]">{tid}</span>
                          <span className="text-[#a0aabf]">→ {info.target_db}</span>
                          <span className="font-semibold">{info.total?.toLocaleString?.() ?? info.total} rows</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
                <details className="text-[10px]">
                  <summary className="cursor-pointer text-[#a0aabf] hover:underline py-1">Raw JSON (for support)</summary>
                  <pre className="mt-2 bg-black/40 rounded p-3 overflow-x-auto text-[#cbd2df] max-h-96 overflow-y-auto">
                    {JSON.stringify(report, null, 2)}
                  </pre>
                </details>
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
